import React, { useEffect, useState } from 'react';
import { translations } from '../translations';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/apiService';
import { getDaysSinceStart } from '../services/dateService';
import HighchartsMeasurementGraph from './HighchartsMeasurementGraph';
import { formatString } from '../translations';
import Layout from './Layout';

function Account({ patientData, language = 'en', isActive = false }) {
  const navigate = useNavigate();
  const t = translations[language] || translations.en;
  const [overallProgress, setOverallProgress] = useState(null);
  const [expectedProgress, setExpectedProgress] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expectedTasks, setExpectedTasks] = useState(null);
  const [completedTasks, setCompletedTasks] = useState(null);
  const [selectedMeasurementType, setSelectedMeasurementType] = useState('weight');
  const [weightMeasurements, setWeightMeasurements] = useState([]);
  const [waistMeasurements, setWaistMeasurements] = useState([]);
  const [ketoneMeasurements, setKetoneMeasurements] = useState([]);
  const [sbpMeasurements, setSbpMeasurements] = useState([]);
  const [dbpMeasurements, setDbpMeasurements] = useState([]);
  const [pulseMeasurements, setPulseMeasurements] = useState([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [measurementsRefreshTrigger, setMeasurementsRefreshTrigger] = useState(0);
  const [lastProgressData, setLastProgressData] = useState(() => {
    // Récupérer les données en cache du localStorage
    const cached = localStorage.getItem(`progress_${patientData?.patientId}`);
    if (cached) {
      try {
        const data = JSON.parse(cached);
        // Vérifier si les données ne sont pas trop anciennes (24h)
        const isExpired = Date.now() - data.timestamp > 24 * 60 * 60 * 1000;
        if (isExpired) {
          localStorage.removeItem(`progress_${patientData?.patientId}`);
          return null;
        }
        return data;
      } catch (e) {
        localStorage.removeItem(`progress_${patientData?.patientId}`);
        return null;
      }
    }
    return null;
  });

  // Scroll vers le haut quand le composant se monte
  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Ajouter l'animation CSS pour le pulse
    const style = document.createElement('style');
    style.textContent = `
      @keyframes pulse {
        0% { opacity: 1; }
        50% { opacity: 0.5; }
        100% { opacity: 1; }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Scroll vers le haut à chaque activation de l'onglet Account
  useEffect(() => {
    if (isActive) {
      try {
        window.scrollTo({ top: 0, behavior: 'auto' });
      } catch (_) {
        window.scrollTo(0, 0);
      }
    }
  }, [isActive]);
  
  // Mettre à jour le cache quand patientData change
  useEffect(() => {
    if (patientData?.patientId) {
      const cached = localStorage.getItem(`progress_${patientData.patientId}`);
      if (cached) {
        try {
          const data = JSON.parse(cached);
          const isExpired = Date.now() - data.timestamp > 24 * 60 * 60 * 1000;
          if (isExpired) {
            localStorage.removeItem(`progress_${patientData.patientId}`);
            setLastProgressData(null);
          } else {
            setLastProgressData(data);
          }
        } catch (e) {
          localStorage.removeItem(`progress_${patientData.patientId}`);
          setLastProgressData(null);
        }
      }
    }
  }, [patientData?.patientId]);

  // Rafraîchir les données quand l'utilisateur revient sur la page
  useEffect(() => {
    const handleFocus = () => {
      setRefreshTrigger(prev => prev + 1);
      setMeasurementsRefreshTrigger(prev => prev + 1);
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        setRefreshTrigger(prev => prev + 1);
        setMeasurementsRefreshTrigger(prev => prev + 1);
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Calculer le progrès global basé sur les tâches complétées
  useEffect(() => {
    const calculateProgress = async () => {
      if (!patientData || !patientData.patientId || !patientData.startDate) {
        return;
      }
      
      // Toujours afficher immédiatement les données en cache si disponibles
      if (lastProgressData) {
        setOverallProgress(lastProgressData.progress);
        setExpectedProgress(lastProgressData.expectedPercent);
        setExpectedTasks(lastProgressData.expected);
        setCompletedTasks(lastProgressData.completed);
      } else {
        // Au premier chargement, afficher des valeurs par défaut pour éviter le loading
        setOverallProgress(0);
        setExpectedProgress(0);
        setExpectedTasks(0);
        setCompletedTasks(0);
      }
      
      try {
        const response = await apiService.getPatientTimeline(patientData.patientId);
        if (response.success && response.timeline) {
          // Créer un hash des données pour comparer avec les données précédentes
          const currentDataHash = JSON.stringify({
            startDate: patientData.startDate,
            timeline: response.timeline.map(phase => 
              phase.days.map(day => 
                day.tasks.map(task => ({ taskId: task.taskId, completed: task.completed, dayNumber: day.dayNumber }))
              )
            )
          });
          
          // Si les données n'ont pas changé, ne pas recharger
          if (lastProgressData && lastProgressData.hash === currentDataHash) {
            console.log('Progress: Données identiques, pas de rechargement');
            return;
          }
          
          console.log('Progress: Données modifiées, rechargement nécessaire');
          setLoading(true);
          let totalTasks = 0;
          let completed = 0;
          let expected = 0;
          const daysSinceStart = getDaysSinceStart(patientData.startDate);
          // --- Extraction des taskId de mesures complétées ---
          const weightTasks = [];
          const waistTasks = [];
          const ketoneTasks = [];
          const bloodPressureTasks = [];
          response.timeline.forEach(phase => {
            phase.days.forEach(day => {
              day.tasks.forEach(task => {
                // Log tous les taskId et titres pour debug
                console.log('TASK DEBUG:', { taskId: task.taskId, title: task.title, completed: task.completed });
                if (task.taskId && task.taskId.toLowerCase().includes('weight') && task.completed) {
                  weightTasks.push({ taskId: task.taskId, dayNumber: day.dayNumber });
                }
                if (task.taskId && task.taskId.toLowerCase().includes('waist') && task.completed) {
                  waistTasks.push({ taskId: task.taskId, dayNumber: day.dayNumber });
                }
                if (task.taskId && task.taskId.toLowerCase().includes('ketostix') && task.completed) {
                  ketoneTasks.push({ taskId: task.taskId, dayNumber: day.dayNumber });
                }
                if (task.taskId && (task.taskId.toLowerCase().includes('blood_pressure') || task.taskId.toLowerCase().includes('blood-pressure')) && task.completed) {
                  bloodPressureTasks.push({ taskId: task.taskId, dayNumber: day.dayNumber });
                  console.log('Blood pressure task found:', task.taskId, 'Day:', day.dayNumber);
                }
                totalTasks++;
                if (task.completed) {
                  completed++;
                }
                // Règle d'attente alignée sur l'unlock: utiliser la logique d'unlock correcte
                if (typeof day.dayNumber === 'number') {
                  const UNLOCK_SHIFT = 21;
                  const allowableDayNumber = daysSinceStart - UNLOCK_SHIFT;
                  if (day.dayNumber <= allowableDayNumber) {
                    expected++;
                  }
                }
              });
            });
          });
          // --- Appel API pour chaque taskId de mesure ---
          const getMeasurementValues = async (tasks, valueKey, isKetone = false) => {
            const dayMap = new Map(); // Map pour stocker la valeur la plus récente par jour
            
            for (const t of tasks) {
              try {
                const res = await apiService.getQuestionnaireResponse(patientData.patientId, t.taskId);
                let value = undefined;
                if (res && res.success && res.data) {
                  if (isKetone) {
                    // Log pour debug
                    console.log('Réponse cétoniques:', res.data);
                    if (typeof res.data.score === 'number') {
                      value = res.data.score;
                    } else if (res.data.responses) {
                      try {
                        const resp = typeof res.data.responses === 'string' ? JSON.parse(res.data.responses) : res.data.responses;
                        if (resp && typeof resp.ketone_value === 'number') {
                          value = resp.ketone_value;
                        } else if (resp && typeof resp.value === 'number') {
                          value = resp.value;
                        }
                      } catch (e) {}
                    }
                    if (typeof value === 'number') {
                      console.log('Valeur cétoniques trouvée:', value);
                    }
                  } else {
                    if (typeof res.data.score === 'number') {
                      value = res.data.score;
                    } else if (res.data.responses) {
                      try {
                        const resp = typeof res.data.responses === 'string' ? JSON.parse(res.data.responses) : res.data.responses;
                        if (resp && typeof resp[valueKey] === 'number') {
                          value = resp[valueKey];
                        }
                      } catch (e) {}
                    }
                  }
                }
                if (typeof value === 'number') {
                  // Garder seulement la valeur la plus récente pour chaque jour
                  const existingEntry = dayMap.get(t.dayNumber);
                  if (!existingEntry || res.data.completedAt > existingEntry.completedAt) {
                    dayMap.set(t.dayNumber, { dayNumber: t.dayNumber, value, completedAt: res.data.completedAt });
                  }
                }
              } catch (e) {}
            }
            
            // Convertir la Map en array et trier
            const arr = Array.from(dayMap.values());
            arr.sort((a, b) => a.dayNumber - b.dayNumber);
            return arr;
          };
          setWeightMeasurements(await getMeasurementValues(weightTasks, 'body_weight'));
          setWaistMeasurements(await getMeasurementValues(waistTasks, 'waist_circumference'));
          setKetoneMeasurements(await getMeasurementValues(ketoneTasks, 'ketone_value', true));
          
          // Extraction des mesures de tension artérielle
          const getBloodPressureValues = async (tasks) => {
            const sbpMap = new Map(); // Map pour stocker la valeur la plus récente par jour
            const dbpMap = new Map();
            const pulseMap = new Map();
            
            for (const t of tasks) {
              try {
                console.log('Fetching blood pressure data for taskId:', t.taskId);
                const res = await apiService.getQuestionnaireResponse(patientData.patientId, t.taskId);
                console.log('Blood pressure response:', res); // Debug
                if (res && res.success && res.data && res.data.responses) {
                  try {
                    const resp = typeof res.data.responses === 'string' ? JSON.parse(res.data.responses) : res.data.responses;
                    console.log('Parsed blood pressure data:', resp); // Debug
                    
                    // Vérifier si les valeurs sont des nombres valides et garder la plus récente
                    if (resp && resp.sbp && !isNaN(parseInt(resp.sbp))) {
                      const existingSbp = sbpMap.get(t.dayNumber);
                      if (!existingSbp || res.data.completedAt > existingSbp.completedAt) {
                        sbpMap.set(t.dayNumber, { dayNumber: t.dayNumber, value: parseInt(resp.sbp), completedAt: res.data.completedAt });
                      }
                    }
                    if (resp && resp.dbp && !isNaN(parseInt(resp.dbp))) {
                      const existingDbp = dbpMap.get(t.dayNumber);
                      if (!existingDbp || res.data.completedAt > existingDbp.completedAt) {
                        dbpMap.set(t.dayNumber, { dayNumber: t.dayNumber, value: parseInt(resp.dbp), completedAt: res.data.completedAt });
                      }
                    }
                    if (resp && resp.pulse && !isNaN(parseInt(resp.pulse))) {
                      const existingPulse = pulseMap.get(t.dayNumber);
                      if (!existingPulse || res.data.completedAt > existingPulse.completedAt) {
                        pulseMap.set(t.dayNumber, { dayNumber: t.dayNumber, value: parseInt(resp.pulse), completedAt: res.data.completedAt });
                      }
                    }
                  } catch (e) {
                    console.error('Erreur parsing blood pressure data:', e);
                  }
                }
              } catch (e) {
                console.error('Erreur récupération blood pressure data:', e);
              }
            }
            
            // Convertir les Maps en arrays et trier
            const sbpArr = Array.from(sbpMap.values());
            const dbpArr = Array.from(dbpMap.values());
            const pulseArr = Array.from(pulseMap.values());
            
            console.log('SBP array:', sbpArr); // Debug
            console.log('DBP array:', dbpArr); // Debug
            console.log('Pulse array:', pulseArr); // Debug
            
            sbpArr.sort((a, b) => a.dayNumber - b.dayNumber);
            dbpArr.sort((a, b) => a.dayNumber - b.dayNumber);
            pulseArr.sort((a, b) => a.dayNumber - b.dayNumber);
            return { sbpArr, dbpArr, pulseArr };
          };
          
          console.log('Blood pressure tasks found:', bloodPressureTasks);
          const bloodPressureData = await getBloodPressureValues(bloodPressureTasks);
          setSbpMeasurements(bloodPressureData.sbpArr);
          setDbpMeasurements(bloodPressureData.dbpArr);
          setPulseMeasurements(bloodPressureData.pulseArr);
          // --- Fin extraction mesures ---
          const progress = totalTasks > 0 ? Math.round((completed / totalTasks) * 100) : 0;
          const expectedPercent = totalTasks > 0 ? Math.round((expected / totalTasks) * 100) : 0;
          setOverallProgress(progress);
          setExpectedProgress(expectedPercent);
          setExpectedTasks(expected);
          setCompletedTasks(completed);
          
          // Sauvegarder les données actuelles pour la prochaine comparaison et dans localStorage
          const progressData = {
            hash: currentDataHash,
            progress,
            expectedPercent,
            expected,
            completed,
            timestamp: Date.now()
          };
          setLastProgressData(progressData);
          localStorage.setItem(`progress_${patientData.patientId}`, JSON.stringify(progressData));
        }
      } catch (error) {
        console.error('Erreur calcul progrès:', error);
        setOverallProgress(null);
        setExpectedProgress(null);
        setExpectedTasks(null);
        setCompletedTasks(null);
        setWeightMeasurements([]);
        setWaistMeasurements([]);
        setKetoneMeasurements([]);
        setSbpMeasurements([]);
        setDbpMeasurements([]);
        setPulseMeasurements([]);
      } finally {
        setLoading(false);
      }
    };

    calculateProgress();
  }, [patientData, refreshTrigger]);

  // useEffect séparé pour les mesures uniquement
  useEffect(() => {
    const loadMeasurements = async () => {
      if (!patientData || !patientData.patientId || !patientData.startDate) {
        return;
      }
      try {
        const response = await apiService.getPatientTimeline(patientData.patientId);
        if (response.success && response.timeline) {
          // --- Extraction des taskId de mesures complétées ---
          const weightTasks = [];
          const waistTasks = [];
          const ketoneTasks = [];
          const bloodPressureTasks = [];
          response.timeline.forEach(phase => {
            phase.days.forEach(day => {
              day.tasks.forEach(task => {
                if (task.taskId && task.taskId.toLowerCase().includes('weight') && task.completed) {
                  weightTasks.push({ taskId: task.taskId, dayNumber: day.dayNumber });
                }
                if (task.taskId && task.taskId.toLowerCase().includes('waist') && task.completed) {
                  waistTasks.push({ taskId: task.taskId, dayNumber: day.dayNumber });
                }
                if (task.taskId && task.taskId.toLowerCase().includes('ketostix') && task.completed) {
                  ketoneTasks.push({ taskId: task.taskId, dayNumber: day.dayNumber });
                }
                if (task.taskId && (task.taskId.toLowerCase().includes('blood_pressure') || task.taskId.toLowerCase().includes('blood-pressure')) && task.completed) {
                  bloodPressureTasks.push({ taskId: task.taskId, dayNumber: day.dayNumber });
                }
              });
            });
          });
          
          // --- Appel API pour chaque taskId de mesure ---
          const getMeasurementValues = async (tasks, valueKey, isKetone = false) => {
            const dayMap = new Map(); // Map pour stocker la valeur la plus récente par jour
            
            for (const t of tasks) {
              try {
                const res = await apiService.getQuestionnaireResponse(patientData.patientId, t.taskId);
                let value = undefined;
                if (res && res.success && res.data) {
                  if (isKetone) {
                    if (typeof res.data.score === 'number') {
                      value = res.data.score;
                    } else if (res.data.responses) {
                      try {
                        const resp = typeof res.data.responses === 'string' ? JSON.parse(res.data.responses) : res.data.responses;
                        if (resp && typeof resp.ketone_value === 'number') {
                          value = resp.ketone_value;
                        } else if (resp && typeof resp.value === 'number') {
                          value = resp.value;
                        }
                      } catch (e) {}
                    }
                  } else {
                    if (typeof res.data.score === 'number') {
                      value = res.data.score;
                    } else if (res.data.responses) {
                      try {
                        const resp = typeof res.data.responses === 'string' ? JSON.parse(res.data.responses) : res.data.responses;
                        if (resp && typeof resp[valueKey] === 'number') {
                          value = resp[valueKey];
                        }
                      } catch (e) {}
                    }
                  }
                }
                if (typeof value === 'number') {
                  // Garder seulement la valeur la plus récente pour chaque jour
                  const existingEntry = dayMap.get(t.dayNumber);
                  if (!existingEntry || res.data.completedAt > existingEntry.completedAt) {
                    dayMap.set(t.dayNumber, { dayNumber: t.dayNumber, value, completedAt: res.data.completedAt });
                  }
                }
              } catch (e) {}
            }
            
            // Convertir la Map en array et trier
            const arr = Array.from(dayMap.values());
            arr.sort((a, b) => a.dayNumber - b.dayNumber);
            return arr;
          };
          
          // Extraction des mesures de tension artérielle
          const getBloodPressureValues = async (tasks) => {
            const sbpMap = new Map(); // Map pour stocker la valeur la plus récente par jour
            const dbpMap = new Map();
            const pulseMap = new Map();
            
            for (const t of tasks) {
              try {
                const res = await apiService.getQuestionnaireResponse(patientData.patientId, t.taskId);
                if (res && res.success && res.data && res.data.responses) {
                  try {
                    const resp = typeof res.data.responses === 'string' ? JSON.parse(res.data.responses) : res.data.responses;
                    
                    // Vérifier si les valeurs sont des nombres valides et garder la plus récente
                    if (resp && resp.sbp && !isNaN(parseInt(resp.sbp))) {
                      const existingSbp = sbpMap.get(t.dayNumber);
                      if (!existingSbp || res.data.completedAt > existingSbp.completedAt) {
                        sbpMap.set(t.dayNumber, { dayNumber: t.dayNumber, value: parseInt(resp.sbp), completedAt: res.data.completedAt });
                      }
                    }
                    if (resp && resp.dbp && !isNaN(parseInt(resp.dbp))) {
                      const existingDbp = dbpMap.get(t.dayNumber);
                      if (!existingDbp || res.data.completedAt > existingDbp.completedAt) {
                        dbpMap.set(t.dayNumber, { dayNumber: t.dayNumber, value: parseInt(resp.dbp), completedAt: res.data.completedAt });
                      }
                    }
                    if (resp && resp.pulse && !isNaN(parseInt(resp.pulse))) {
                      const existingPulse = pulseMap.get(t.dayNumber);
                      if (!existingPulse || res.data.completedAt > existingPulse.completedAt) {
                        pulseMap.set(t.dayNumber, { dayNumber: t.dayNumber, value: parseInt(resp.pulse), completedAt: res.data.completedAt });
                      }
                    }
                  } catch (e) {
                    console.error('Erreur parsing blood pressure data:', e);
                  }
                }
              } catch (e) {
                console.error('Erreur récupération blood pressure data:', e);
              }
            }
            
            // Convertir les Maps en arrays et trier
            const sbpArr = Array.from(sbpMap.values());
            const dbpArr = Array.from(dbpMap.values());
            const pulseArr = Array.from(pulseMap.values());
            
            sbpArr.sort((a, b) => a.dayNumber - b.dayNumber);
            dbpArr.sort((a, b) => a.dayNumber - b.dayNumber);
            pulseArr.sort((a, b) => a.dayNumber - b.dayNumber);
            return { sbpArr, dbpArr, pulseArr };
          };
          
          setWeightMeasurements(await getMeasurementValues(weightTasks, 'body_weight'));
          setWaistMeasurements(await getMeasurementValues(waistTasks, 'waist_circumference'));
          setKetoneMeasurements(await getMeasurementValues(ketoneTasks, 'ketone_value', true));
          
          const bloodPressureData = await getBloodPressureValues(bloodPressureTasks);
          setSbpMeasurements(bloodPressureData.sbpArr);
          setDbpMeasurements(bloodPressureData.dbpArr);
          setPulseMeasurements(bloodPressureData.pulseArr);
        }
      } catch (error) {
        console.error('Erreur chargement mesures:', error);
        setWeightMeasurements([]);
        setWaistMeasurements([]);
        setKetoneMeasurements([]);
        setSbpMeasurements([]);
        setDbpMeasurements([]);
        setPulseMeasurements([]);
      }
    };

    loadMeasurements();
  }, [patientData, measurementsRefreshTrigger]);

  // Fonction pour aller aux tâches du jour
  const goToTodayTasks = () => {
    // Navigation vers la page du jour actuel
    // À implémenter selon la logique de navigation
    console.log('Navigate to today tasks');
  };

  return (
    <div className="account-container">
      <div className="account-content">
        {/* Study Information */}
        <div className="account-section">
          <div className="study-info">
            <p className="study-description">
              {t.account.studyDescription}
            </p>
            <h3 className="study-name">
              "{t.account.studyName}"
            </h3>
            <p className="study-thanks">
              {t.account.studyThanks}
            </p>
          </div>
        </div>

        {/* Progress Section */}
        <div className="account-section">
          <h2 className="section-title">{t.account.progress}</h2>
          <div className="progress-display" style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
            {overallProgress === null ? (
              <div className="progress-loading">
                <div className="loading-spinner">{t.loading || t.account?.loading || 'Chargement...'}</div>
              </div>
            ) : (
              <>
                <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center'}}>
                  {/* Pourcentage réel à gauche */}
                  <div style={{minWidth: 50, textAlign: 'right', marginRight: 8}}>
                    <span style={{color: '#3b82f6', fontWeight: 600, fontSize: '1.2em'}}>{overallProgress}%</span>
                  </div>
                  {/* Cercle double jauge + texte centré dessous */}
                  <div className="progress-circle-large" style={{position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                    {loading && (
                      <div style={{
                        position: 'absolute',
                        top: -5,
                        right: -5,
                        width: '10px',
                        height: '10px',
                        backgroundColor: '#3b82f6',
                        borderRadius: '50%',
                        animation: 'pulse 1s infinite',
                        border: '2px solid white',
                        boxShadow: '0 0 4px rgba(59, 130, 246, 0.5)'
                      }} />
                    )}
              <svg width="120" height="120" viewBox="0 0 120 120">
                      {/* Cercle de fond */}
                <circle
                  cx="60"
                  cy="60"
                  r="50"
                  fill="none"
                  stroke="#e5e7eb"
                  strokeWidth="8"
                />
                      {/* Jauge attendue (arrière-plan, bleu clair) */}
                      <circle
                        cx="60"
                        cy="60"
                        r="50"
                        fill="none"
                        stroke="#93c5fd"
                        strokeWidth="8"
                        strokeDasharray={`${2 * Math.PI * 50}`}
                        strokeDashoffset={`${2 * Math.PI * 50 * (1 - expectedProgress / 100)}`}
                        transform="rotate(-90 60 60)"
                        style={{ transition: 'stroke-dashoffset 0.3s' }}
                      />
                      {/* Jauge réelle (avant-plan, bleu vif) */}
                <circle
                  cx="60"
                  cy="60"
                  r="50"
                  fill="none"
                  stroke="#3b82f6"
                  strokeWidth="8"
                  strokeDasharray={`${2 * Math.PI * 50}`}
                  strokeDashoffset={`${2 * Math.PI * 50 * (1 - overallProgress / 100)}`}
                  transform="rotate(-90 60 60)"
                        style={{ transition: 'stroke-dashoffset 0.3s' }}
                />
              </svg>
                    {/* Texte centré sous le cercle */}
                    <div style={{width: '100%', textAlign: 'center', marginTop: 4}}>
                      {overallProgress === expectedProgress ? (
                        <span style={{color: '#22c55e', fontSize: '0.85em'}}>{t.account.upToDate}</span>
                      ) : (
                        <span style={{color: '#93c5fd', fontSize: '0.95em'}}>
                          {(() => {
                            const count = expectedTasks - completedTasks;
                            const forms = t.account.tasksToDo.split('|');
                            const label = count === 1 ? forms[0] : (forms[1] || forms[0]);
                            return formatString(label, { count });
                          })()}
                        </span>
                      )}
                    </div>
                  </div>
                  {/* À droite : soit le pourcentage attendu, soit "Pending" */}
                  <div style={{minWidth: 50, textAlign: 'left', marginLeft: 8, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center'}}>
                    {overallProgress === expectedProgress ? (
                      <span style={{color: '#93c5fd', fontWeight: 600, fontSize: '1.2em'}}>{expectedProgress === 100 ? '' : expectedProgress + '%'}</span>
                    ) : (
                      <span style={{color: '#93c5fd', fontWeight: 500, fontSize: '0.85em', marginTop: 2, marginLeft: 2}}>{t.account.pending}</span>
                    )}
              </div>
            </div>
              </>
            )}
          </div>
        </div>



        {/* Evaluation Section */}
        <div className="account-section">
          <h2 className="section-title">{t.account.measurements}</h2>
          <div style={{marginBottom: 10, display: 'flex', alignItems: 'center', gap: '8px'}}>
            {/* Icône correspondante au type de mesure sélectionné */}
            <img 
              src={`/images/${selectedMeasurementType === 'weight' ? 'balance-corporelle-icon.png' : 
                         selectedMeasurementType === 'waist' ? 'tour-taille-icon.png' : 
                         selectedMeasurementType === 'ketones' ? 'test-durine.png' : 
                         selectedMeasurementType === 'sbp' || selectedMeasurementType === 'dbp' || selectedMeasurementType === 'pulse' ? 'pression-arterielle-icon.png' : 
                         'questionnaire-icon.png'}`}
              alt="Measurement icon"
              style={{
                width: '20px',
                height: '20px',
                objectFit: 'contain', 
              }}
            />
            <select
              id="measurement-type-select"
              value={selectedMeasurementType}
              onChange={e => {
                setSelectedMeasurementType(e.target.value);
                // Forcer le rafraîchissement des données de mesures uniquement
                setMeasurementsRefreshTrigger(prev => prev + 1);
              }}
              style={{
                border: 'none',
                borderBottom: '1px solid #5bc2e7',
                outline: 'none',
                background: 'transparent',
                fontSize: '1.1rem',
                padding: '2px 8px',
                marginLeft: 10,
                color: '#222',
                marginBottom: 0,
                marginTop: 5
              }}
            >
              <option value="weight">{t.account.weightLabel}</option>
              <option value="waist">{t.account.waistLabel}</option>
              <option value="ketones">{t.account.ketonesLabel}</option>
              <option value="sbp">SBP (mmHg)</option>
              <option value="dbp">DBP (mmHg)</option>
              <option value="pulse">Puls (/min)</option>
            </select>
            </div>
          <div style={{width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
            {/* Affichage dynamique selon le type sélectionné */}
            {selectedMeasurementType === 'weight' && (
              <HighchartsMeasurementGraph 
                measurements={weightMeasurements}
                measurementType="weight"
                language={language}
                startDate={patientData?.startDate}
              />
            )}
            {selectedMeasurementType === 'waist' && (
              <HighchartsMeasurementGraph 
                measurements={waistMeasurements}
                measurementType="waist"
                language={language}
                startDate={patientData?.startDate}
              />
            )}
            {selectedMeasurementType === 'ketones' && (
              <HighchartsMeasurementGraph 
                measurements={ketoneMeasurements}
                measurementType="ketones"
                language={language}
                startDate={patientData?.startDate}
              />
            )}
            {selectedMeasurementType === 'sbp' && (
              <HighchartsMeasurementGraph 
                measurements={sbpMeasurements}
                measurementType="sbp"
                language={language}
                startDate={patientData?.startDate}
              />
            )}
            {selectedMeasurementType === 'dbp' && (
              <HighchartsMeasurementGraph 
                measurements={dbpMeasurements}
                measurementType="dbp"
                language={language}
                startDate={patientData?.startDate}
              />
            )}
            {selectedMeasurementType === 'pulse' && (
              <HighchartsMeasurementGraph 
                measurements={pulseMeasurements}
                measurementType="pulse"
                language={language}
                startDate={patientData?.startDate}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Account; 